// See IMAGE_ENCRYPTION_IMPL.md
import { useRef, useEffect, useCallback } from 'react';

const KEY_HEX = process.env.REACT_APP_ENCRYPTION_KEY;

let _keyPromise = null;
function getKey() {
  if (!_keyPromise) {
    const bytes = new Uint8Array(KEY_HEX.match(/.{2}/g).map(b => parseInt(b, 16)));
    _keyPromise = crypto.subtle.importKey('raw', bytes, { name: 'AES-GCM' }, false, ['decrypt']);
  }
  return _keyPromise;
}

// Decoded image cache — persists across page navigations within the session.
const _cache = new Map();
// In-flight downloads, shared by every canvas asking for the same src meanwhile.
const _pending = new Map();

function fetchDecrypted(src) {
  if (!_pending.has(src)) {
    const promise = (async () => {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`${response.status}`);
      const buffer = await response.arrayBuffer();

      const key = await getKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: buffer.slice(0, 12) },
        key,
        buffer.slice(12)
      );
      _cache.set(src, decrypted);
      return decrypted;
    })().finally(() => _pending.delete(src));
    _pending.set(src, promise);
  }
  return _pending.get(src);
}

// Draw bitmap filling canvas with cover crop (simulates object-fit: cover).
function drawCover(ctx, bitmap, w, h) {
  const scale = Math.max(w / bitmap.width, h / bitmap.height);
  const srcW  = w / scale;
  const srcH  = h / scale;
  const srcX  = (bitmap.width  - srcW) / 2;
  const srcY  = (bitmap.height - srcH) / 2;
  ctx.imageSmoothingQuality = 'high'; // default 'low' aliases when downscaling a large hero
  ctx.drawImage(bitmap, srcX, srcY, srcW, srcH, 0, 0, w, h);
}

// Size the canvas to its CSS box and draw in cover mode. The backing store follows
// devicePixelRatio (sharp small thumbnails on HiDPI screens), capped at the bitmap's
// own resolution so a full-screen hero doesn't allocate a canvas larger than the photo.
function fitCover(canvas, bitmap) {
  const w = canvas.offsetWidth  || bitmap.width;
  const h = canvas.offsetHeight || bitmap.height;
  const dpr = Math.min(
    window.devicePixelRatio || 1,
    Math.max(bitmap.width / w, bitmap.height / h, 1)
  );
  canvas.width  = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  drawCover(canvas.getContext('2d'), bitmap, canvas.width, canvas.height);
}

const EncryptedImage = ({ src, alt, className, style, ratio, cover, onLoad, onContextMenu, onDragStart }) => {
  const canvasRef   = useRef(null);
  const loadedRef   = useRef(false);
  const bitmapRef   = useRef(null); // kept alive in cover mode for resize redraws
  // Callers pass inline handlers: reading onLoad through a ref keeps draw/load stable,
  // so a parent re-render doesn't re-run the loading effect and restart the download.
  const onLoadRef   = useRef(onLoad);
  onLoadRef.current = onLoad;

  const prevent = (e) => e.preventDefault();

  // Cleanup bitmap on unmount (cover mode only)
  useEffect(() => {
    return () => {
      if (bitmapRef.current) {
        bitmapRef.current.close();
        bitmapRef.current = null;
      }
    };
  }, []);

  const draw = useCallback(async (decrypted) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const bitmap = await createImageBitmap(new Blob([decrypted], { type: 'image/jpeg' }));

    if (cover) {
      fitCover(canvas, bitmap);
      bitmapRef.current = bitmap; // keep alive for resize redraws
    } else {
      canvas.width  = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0);
      bitmap.close();

      canvas.style.removeProperty('aspect-ratio');
      canvas.style.removeProperty('background');
    }

    loadedRef.current = true;

    onLoadRef.current?.({ target: { naturalWidth: bitmap.width, naturalHeight: bitmap.height } });
  }, [cover]);

  const load = useCallback(async () => {
    if (!src || !canvasRef.current || loadedRef.current) return;

    try {
      const decrypted = _cache.get(src) || await fetchDecrypted(src);
      await draw(decrypted);
    } catch (e) {
      console.warn('Image load failed:', src, e);
    }
  }, [src, draw]);

  // Cover mode: redraw on container resize
  useEffect(() => {
    if (!cover) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      const bitmap = bitmapRef.current;
      if (!bitmap || !canvas.offsetWidth || !canvas.offsetHeight) return;
      fitCover(canvas, bitmap);
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [cover]);

  // Lazy load via IntersectionObserver
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    loadedRef.current = false;

    if (_cache.has(src)) {
      load();
      return;
    }

    if (!cover && ratio) {
      canvas.width  = 1920;
      canvas.height = Math.round(1920 * ratio);
      canvas.style.setProperty('aspect-ratio', `1 / ${ratio}`);
      canvas.style.setProperty('background', '#111');
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          load();
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [src, ratio, cover, load]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={style}
      role="img"
      aria-label={alt}
      onContextMenu={onContextMenu ?? prevent}
      onDragStart={onDragStart ?? prevent}
    />
  );
};

export default EncryptedImage;